import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { WindowComponent } from '@ibc-shared/components/window/window.component';
import { TranslateModule } from '@ngx-translate/core';
import { User } from '../models/user.model';
import { UserService } from '../services/users.service';

@Component({
  selector: 'app-register',
  imports: [ReactiveFormsModule, RouterModule, TranslateModule, NgTemplateOutlet, WindowComponent],
  templateUrl: './register.component.html',
  styleUrl: './register.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RegisterComponent {
  private readonly userService = inject(UserService);

  private readonly serverMsgKeyMap: Record<string, string> = {
    'User already exists': 'features.register.errors.userAlreadyExists',
  };

  protected registrationComplete = signal<boolean>(false);
  protected apiErrorMessage = signal<string>('');

  protected preferredLanguages = [
    { code: 'es', label: 'Español' },
    { code: 'en', label: 'English' },
    { code: 'pt', label: 'Português' },
  ];

  protected form = new FormGroup(
    {
      email: new FormControl('', [Validators.required, Validators.email]),
      name: new FormControl('', [Validators.required]),
      birthday: new FormControl('', [Validators.required, this.ageValidator]),
      nickname: new FormControl('', [Validators.required]),
      password: new FormControl('', [Validators.required, Validators.minLength(8)]),
      repeat_password: new FormControl('', [Validators.required]),
      language: new FormControl('', [Validators.required]),
      acceptedTerms: new FormControl(false, [Validators.requiredTrue]),
      acceptedPrivacy: new FormControl(false, [Validators.requiredTrue]),
    },
    { validators: this.passwordMatchValidator }
  );

  private ageValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;

    const birthDate = new Date(control.value);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age >= 18 ? null : { underage: { requiredAge: 18, actualAge: age } };
  }

  private passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
    const password = control.get('password')?.value;
    const repeatPassword = control.get('repeat_password')?.value;

    if (!password || !repeatPassword) return null;

    return password === repeatPassword ? null : { passwordMismatch: true };
  }

  protected getTodayDate(): string {
    return new Date().toISOString().split('T')[0];
  }

  protected getMinBirthdate(): string {
    const today = new Date();
    today.setFullYear(today.getFullYear() - 100);
    return today.toISOString().split('T')[0];
  }

  protected getErrorMessage(controlName: string): string {
    const control = this.form.get(controlName);
    if (!control || !control.errors || !control.touched) return '';

    const errors = control.errors;

    if (errors['serverKey']) return errors['serverKey'];

    if (errors['required']) return 'features.register.errors.requiredField';

    switch (controlName) {
      case 'email':
        if (errors['email']) return 'features.register.errors.invalidEmail';
        break;
      case 'birthday':
        if (errors['underage']) return 'features.register.errors.underageUser';
        break;
      case 'password':
        if (errors['minlength']) return 'features.register.errors.passwordMinLength';
        break;
    }
    return '';
  }

  get passwordMismatch(): boolean {
    return (this.form.hasError('passwordMismatch') && this.form.get('repeat_password')?.touched) || false;
  }

  protected hasError(controlName: string): boolean {
    const control = this.form.get(controlName);
    return !!(control && control.errors && control.touched);
  }

  protected onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const formValue = this.form.value;

    const newUser: User = {
      name: formValue.name ?? '',
      email: formValue.email ?? '',
      birthdate: formValue.birthday ?? '',
      username: formValue.nickname ?? '',
      displayName: formValue.nickname ?? '',
      password: formValue.password ?? '',
      language: formValue.language ?? '',
      acceptedPrivacy: formValue.acceptedPrivacy ?? false,
      acceptedTerms: formValue.acceptedTerms ?? false,
    };

    this.userService.createUser(newUser).subscribe({
      next: () => {
        this.registrationComplete.set(true);
      },
      error: err => {
        const key = this.toI18nKey(err?.error?.message || err?.message);
        this.apiErrorMessage.set(key);
        if (err.status === 409) {
          this.markFieldServerKey('email', 'features.register.errors.userAlreadyExists');
        } else {
          this.markFieldServerKey('email', 'features.register.errors.generic');
        }

        console.error('Error registering the user', err);
      },
    });
  }

  /* HELPERS */
  private toI18nKey(msg: string | undefined | null): string {
    return this.serverMsgKeyMap[msg ?? ''] ?? 'features.register.errors.generic';
  }

  private markFieldServerKey(field: string, key: string) {
    const c = this.form.get(field);
    c?.setErrors({ ...(c?.errors || {}), serverKey: key });
    c?.markAsTouched();
  }
}
